import type { PricePoint, RenderMode } from '@/api/types';
import { cn } from '@/lib/cn';
import { formatDropRatio, formatMoney, formatRiseRatio } from '@/lib/format';
import { canShowPercent } from '@/lib/series';
import { invariant } from '@/lib/verdict';

const GONE: Partial<Record<PricePoint['availability'], string>> = {
  sold_out: 'мест нет',
  not_found: 'вариант исчез',
};

export function Money({
  price,
  strikeWhenGone = false,
}: {
  price: PricePoint | null;
  strikeWhenGone?: boolean;
}) {
  if (!price) return <span className="text-ink-dim">—</span>;

  const goneLabel = strikeWhenGone ? GONE[price.availability] : undefined;

  // T50: цена не переносится и набрана табличными цифрами — колонка цен это
  // главное, что сравнивают глазами, и «дрожание» разрядов построчно мешает
  // ровно этому. В таблице `tabular-nums` задан на `table`, здесь — чтобы
  // работало и в карточках узкого экрана.
  return (
    <span className="inline-flex items-baseline gap-2 whitespace-nowrap tabular-nums">
      <span className={cn('text-ink', goneLabel && 'line-through text-ink-dim')}>
        {formatMoney(price.total, price.currency)}
      </span>
      {goneLabel ? <span className="text-[11px] text-ink-dim">{goneLabel}</span> : null}
    </span>
  );
}

/**
 * Изменение цены с прошлого замера — падение ИЛИ рост.
 *
 * Контракт §3.4: `StayListItem` несёт `drop_ratio` и `rise_ratio`; обе доли
 * положительны, ровно одна из них не-null, либо обе null при нехватке истории.
 * Считает СЕРВЕР. Клиент не выводит направление из `spark[]` — ряд там усечён,
 * и вычисление по нему было бы ровно тем враньём, ради запрета которого
 * написан §5 спеки (Global Constraint 15).
 *
 * §5.1: процент не существует, пока замер один. Решение принимается по
 * render_mode СЕРВЕРА, не по числу поездок и не по длине массива (блокер №8).
 *
 * §4.1: вспышка положена только срыву. Рост подсвечен быть не может даже при
 * isBreakdown — подорожание не является событием срыва по определению.
 *
 * Д-Ф16: одновременно не-null — не «данные», а расхождение с контрактом.
 * Раньше здесь стояло «падение приоритетнее»: тихое правило, которое сделало бы
 * нарушение невидимым навсегда. Теперь оно падает в разработке и тестах.
 */
export function ChangeRatio({
  dropRatio,
  riseRatio = null,
  mode,
  isBreakdown = false,
}: {
  dropRatio: number | null;
  riseRatio?: number | null;
  mode: RenderMode;
  isBreakdown?: boolean;
}) {
  invariant(
    dropRatio === null || riseRatio === null,
    `drop_ratio и rise_ratio одновременно не-null (${dropRatio} / ${riseRatio}): ` +
      'контракт §3.4 это исключает. Расходятся app/api/contracts.py и детектор.',
  );

  if (!canShowPercent(mode)) return null;

  // Сюда исполнение доходит только в проде и только при уже залогированном
  // нарушении: показываем падение — оно и есть смысл продукта.
  const isDrop = dropRatio !== null;
  const ratio = isDrop ? dropRatio : riseRatio;
  if (ratio === null) return null;

  const flare = isDrop && isBreakdown;

  // 🔴 T50: было `font-display text-lg` — процент выходил КРУПНЕЕ самой цены и
  // перетягивал внимание с суммы, которую человек и сравнивает («цены большие,
  // проценты ещё больше»). Он же ломал ячейку: цена вида `160 153 ₽` рядом с
  // крупным `−32%` не помещалась и переносилась на вторую строку, а строка
  // таблицы вырастала. Теперь это подпись при цене: мельче, без дисплейного
  // шрифта, в одну строку. Смысл сохранён — падение по-прежнему отличимо
  // цветом-вспышкой у подтверждённого срыва (Д-В9).
  return (
    <span
      data-direction={isDrop ? 'drop' : 'rise'}
      className={cn(
        // Дисплейная гарнитура сохранена (§4.3 спеки: величина изменения —
        // акцент), изменён только КЕГЛЬ: было `text-lg`, то есть крупнее цены.
        'font-display text-[12px] leading-none whitespace-nowrap tabular-nums',
        flare ? 'text-flare' : 'text-ink-dim',
      )}
      {...(flare ? { 'data-flare': 'true' } : {})}
    >
      {isDrop ? formatDropRatio(ratio) : formatRiseRatio(ratio)}
    </span>
  );
}
